import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';

import { convertAdminRoleToFlag } from '@/vuex/modules/auth/auth.helpers';
import {
    bulkDeleteUsers,
    bulkUpdateUserPermissions,
    createUser,
    fetchUser,
    fetchUsers,
    updateUser,
    updateFeedbackVendorUserForUser,
    updateUserFlag,
    updateUserFeatureFlag,
    updateAdoptManagerFlagAsSuper,
} from './users.utils';
import {
    buildBulkDeleteRequestId,
    getBulkDeleteStatus,
    pollBulkDeleteStatus,
} from './bulk-delete-poll.utils';
import {
    buildBulkPermissionRequestId,
    getBulkPermissionStatus,
    pollBulkPermissionStatus,
} from './bulk-permission-poll.utils';

export default {
    namespaced: true,
    state: {
        error: null,
        fetching: false,
        inviteStatus: null,
        map: {},
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { user }) {
            state.map[user.id] = user;
        },
        deleteMapAtKey (state, { id }) {
            delete state.map[id];
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setInviteStatus (state, { inviteStatus }) {
            state.inviteStatus = inviteStatus;
        },
    },
    actions: {
        async loadAll ({ state, commit }, { reFetch = false } = {}) {
            if (!isEmpty(state.map) && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });

            try {
                const userList = await fetchUsers();
                commit('setMap', { map: keyBy(userList, 'id') });
            } catch (error) {
                commit('setError', { error });
            }

            commit('setFetching', { fetching: false });
        },
        // Load single user
        async loadOne ({ commit, state }, { reFetch = false, id, permissionSets = false } = {}) {
            // Check if loaded in state unless force reFetch is true
            if (state.map[id] && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });
            try {
                const user = await fetchUser({ id, permissionSets });
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', { user });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async invite ({ commit }, user) {
            try {
                const { status } = await createUser(user);
                if (status === 202) {
                    commit('setInviteStatus', { inviteStatus: 'sent' });
                } else {
                    commit('setInviteStatus', { inviteStatus: 'added' });
                }
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async remove ({ commit }, { id }) {
            commit('deleteMapAtKey', { id });
        },
        async submitBulkRemove ({ rootState }, userIds) {
            const subscriptionId = rootState.subscription?.active?.id;
            const userId = rootState.auth?.user?.id;

            const { requestId, conflict } = await bulkDeleteUsers(userIds);

            if (conflict) {
                return {
                    requestId: buildBulkDeleteRequestId({ subscriptionId, userId }),
                    conflict: true,
                };
            }

            return { requestId, conflict: false };
        },
        async pollAndCommitBulkRemove ({ commit }, payload) {
            const { requestId, signal } = typeof payload === 'string'
                ? { requestId: payload }
                : payload;

            const result = await pollBulkDeleteStatus({ requestId, signal });

            if (result.status === 'failed') {
                throw new Error(result.error || 'Bulk delete failed');
            }

            const entries = result.results || [];
            const deleted = [];
            const failed = [];
            for (const entry of entries) {
                if (entry.code === 204) {
                    commit('deleteMapAtKey', { id: entry.userId });
                    deleted.push(entry.userId);
                } else {
                    failed.push(entry);
                }
            }

            return { deleted, failed, total: entries.length };
        },
        async probeForActiveBulkRemove ({ commit, rootState }) {
            const subscriptionId = rootState.subscription?.active?.id;
            const userId = rootState.auth?.user?.id;

            if (!subscriptionId || !userId) {
                return null;
            }

            const requestId = buildBulkDeleteRequestId({ subscriptionId, userId });
            const initial = await getBulkDeleteStatus(requestId);

            if (initial.status === 'none') {
                return null;
            }

            if (initial.status === 'in_progress') {
                return { requestId, terminal: false };
            }

            if (initial.status === 'failed') {
                return {
                    requestId,
                    terminal: true,
                    failedJob: true,
                    failed: [],
                    total: 0,
                    error: initial.error,
                };
            }

            const entries = initial.results || [];
            const failed = [];
            for (const entry of entries) {
                if (entry.code === 204) {
                    commit('deleteMapAtKey', { id: entry.userId });
                } else {
                    failed.push(entry);
                }
            }

            return {
                requestId,
                terminal: true,
                failedJob: false,
                failed,
                total: entries.length,
            };
        },
        async submitBulkUpdatePermissions ({ rootState }, updateRequests) {
            const subscriptionId = rootState.subscription?.active?.id;
            const userId = rootState.auth?.user?.id;

            const { requestId, conflict } = await bulkUpdateUserPermissions(updateRequests);

            if (conflict) {
                return {
                    requestId: buildBulkPermissionRequestId({ subscriptionId, userId }),
                    conflict: true,
                };
            }

            return { requestId, conflict: false };
        },
        async pollAndCommitBulkUpdatePermissions ({ commit }, payload) {
            const { requestId, signal } = typeof payload === 'string'
                ? { requestId: payload }
                : payload;

            const result = await pollBulkPermissionStatus({ requestId, signal });

            if (result.status === 'failed') {
                throw new Error(result.error || 'Bulk permission update failed');
            }

            const entries = result.results || [];
            const succeeded = [];
            const failed = [];
            for (const entry of entries) {
                if (entry.code === 200 && entry.user) {
                    commit('setMapAtKey', { user: entry.user });
                    succeeded.push(entry);
                } else {
                    failed.push(entry);
                }
            }

            return { succeeded, failed, total: entries.length };
        },
        async probeForActiveBulkUpdatePermissions ({ commit, rootState }) {
            const subscriptionId = rootState.subscription?.active?.id;
            const userId = rootState.auth?.user?.id;

            if (!subscriptionId || !userId) {
                return null;
            }

            const requestId = buildBulkPermissionRequestId({ subscriptionId, userId });
            const initial = await getBulkPermissionStatus(requestId);

            if (initial.status === 'none') {
                return null;
            }

            if (initial.status === 'in_progress') {
                return { requestId, terminal: false };
            }

            if (initial.status === 'failed') {
                return {
                    requestId,
                    terminal: true,
                    failedJob: true,
                    succeeded: [],
                    failed: [],
                    total: 0,
                    error: initial.error,
                };
            }

            const entries = initial.results || [];
            const succeeded = [];
            const failed = [];
            for (const entry of entries) {
                if (entry.code === 200 && entry.user) {
                    commit('setMapAtKey', { user: entry.user });
                    succeeded.push(entry);
                } else {
                    failed.push(entry);
                }
            }

            return {
                requestId,
                terminal: true,
                failedJob: false,
                succeeded,
                failed,
                total: entries.length,
            };
        },
        async update ({ commit }, { user: userToUpdate }) {
            commit('setFetching', { fetching: true });

            try {
                // sending applicationRoles indicates this is a v2 request
                // and we DO NOT want to set flags in that case
                const user = userToUpdate.applicationRoles ? userToUpdate : convertAdminRoleToFlag(userToUpdate);

                const updatedUser = await updateUser(user);
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', { user: updatedUser });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async updateFlag ({ commit, dispatch, rootGetters }, { queryParams, setValue, id, subscriptionId }) {
            const restrictedFlags = ['systemUser'];

            if (includes(restrictedFlags, queryParams.name)) {
                return;
            }

            commit('setFetching', { fetching: true });
            try {
                if (queryParams.name === 'adoptManager' && rootGetters['auth/isSuper']) {
                    await updateAdoptManagerFlagAsSuper(id, setValue, subscriptionId);
                } else {
                    await updateUserFlag(id, {
                        flagName: queryParams.name,
                        value: setValue,
                    });
                }
                commit('setFetching', { fetching: false });

                await dispatch('loadOne', { id, reFetch: true });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async updateFeatureFlag ({ commit, dispatch }, { queryParams, setValue, id }) {
            commit('setFetching', { fetching: true });
            try {
                await updateUserFeatureFlag(id, {
                    flagName: queryParams.name,
                    value: setValue,
                });
                commit('setFetching', { fetching: false });

                await dispatch('loadOne', { id, reFetch: true });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async updateFlags ({ commit, dispatch }, { user, flags }) {
            commit('setFetching', { fetching: true });

            try {
                const { falseRequests, trueRequests, adminChangeRequest } = Object.entries(flags).reduce(
                    (acc, flagPair) => {
                        const [flagName, flagValue] = flagPair;
                        if (flagName === 'systemUser') return acc;
                        if (flagName === 'admin') {
                            acc.adminChangeRequest = {
                                flagName,
                                value: flagValue,
                            };

                            return acc;
                        }

                        if (flagValue === true) {
                            acc.trueRequests.push({
                                flagName,
                                value: flagValue,
                            });
                        }

                        if (flagValue === false) {
                            acc.falseRequests.push({
                                flagName,
                                value: flagValue,
                            });
                        }

                        return acc;
                    },
                    {
                        adminChangeRequest: null,
                        falseRequests: [],
                        trueRequests: [],
                    },
                );

                // If we're going to admin, do it first so certain flag combinations are allowed
                if (adminChangeRequest && adminChangeRequest.value === true) {
                    await updateUserFlag(user.id, adminChangeRequest);
                }
                await Promise.all(falseRequests.map((falseRequestPair) => updateUserFlag(user.id, falseRequestPair)));
                await Promise.all(trueRequests.map((trueRequestsPair) => updateUserFlag(user.id, trueRequestsPair)));
                // If we're going from admin, do it last after we removed disallowed certain flag combinations
                if (adminChangeRequest && adminChangeRequest.value === false) {
                    await updateUserFlag(user.id, adminChangeRequest);
                }
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }

            await dispatch('loadOne', { id: user.id, reFetch: true });
        },
        async updateVendorUserForUser ({ commit, dispatch }, { userId, vendorUserPermissionId, first, last }) {
            commit('setFetching', { fetching: true });

            try {
                await updateFeedbackVendorUserForUser({
                    userId,
                    vendorUserPermissionId,
                    first,
                    last,
                });
                await dispatch('loadOne', { id: userId, reFetch: true });
                commit('setFetching', { fetching: false });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
    },
    getters: {
        list: (state) => values(state.map),
        userById: (state) => (id) => state.map[id],
        userByUsername: (state, getters) => (username) => {
            return getters.list.find((user) => user.username.toLowerCase().trim() === username.toLowerCase().trim());
        },
    },
};
